/** Shared utilization helpers mirroring backend TeamOverviewUtilization. */
export const UTILIZATION = {
  Overloaded: "Overloaded",
  High: "High",
  Normal: "Normal",
  Underutilized: "Underutilized",
  NoWorkAssigned: "No Work Assigned",
};

/** Refined status palette — same family, clearer contrast (esp. Not Started vs Review). */
const STATUS_COLOR_OVERRIDES = {
  "not started": "#8592A6",
  notstarted: "#8592A6",
  active: "#3B82F6",
  completed: "#22C55E",
  done: "#22C55E",
  overdue: "#EF4444",
  closed: "#8B5CF6",
  close: "#8B5CF6",
  testing: "#F59E0B",
  review: "#14B8A6",
  "in review": "#14B8A6",
  inreview: "#14B8A6",
};

export const getUtilizationStatusClass = (status) => {
  switch (status) {
    case UTILIZATION.Overloaded:
      return "util-overloaded";
    case UTILIZATION.High:
      return "util-high";
    case UTILIZATION.Underutilized:
      return "util-under";
    case UTILIZATION.NoWorkAssigned:
      return "util-none";
    default:
      return "util-normal";
  }
};

/** Prefer "No Work Assigned" when planned hours are zero. */
export const getDisplayUtilizationStatus = (status, plannedHours) => {
  if (!plannedHours || Number(plannedHours) <= 0) {
    return UTILIZATION.NoWorkAssigned;
  }
  return status || UTILIZATION.Underutilized;
};

export const formatHours = (hours) => {
  if (hours == null || Number.isNaN(Number(hours))) return "0h";
  const totalMinutes = Math.round(Number(hours) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

/**
 * Parse API dates as local calendar dates to avoid UTC shift (e.g. "2026-09-22"
 * becoming the previous day in some timezones).
 */
export const parseApiDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const text = String(value).trim();
  const isoDate = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    return new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]));
  }
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatShortDate = (value) => {
  const date = parseApiDate(value);
  if (!date) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatDateRangeLabel = (fromDate, toDate) => {
  const from = formatShortDate(fromDate);
  const to = formatShortDate(toDate);
  if (from && to) return `${from} – ${to}`;
  return from || to || "";
};

/** Dropdown/chip label: "Sprint 1 (22 Sept 2026 – 28 Sept 2026)". */
export const formatPlanningOptionLabel = (name, startDate, endDate) => {
  const title = (name || "").trim();
  const range = formatDateRangeLabel(startDate, endDate);
  if (title && range) return `${title} (${range})`;
  return title || range || "Planning";
};

export const clampPercent = (value) => {
  const n = Number(value) || 0;
  return Math.max(0, Math.min(100, n));
};

export const toFilterOptions = (items = []) =>
  (items || []).map((item) => ({
    value: item.id,
    label: item.name,
    startDate: item.startDate,
    endDate: item.endDate,
    isLive: item.isLive,
  }));

export const buildDashboardRequest = (teamId, filters) => {
  const planningId = filters.planning?.value ?? null;
  return {
    teamId,
    planningId,
    projectId: filters.project?.value ?? null,
    userId: filters.member?.value ?? null,
    taskStatusId: null,
    taskCategoryId: filters.taskCategory?.value ?? null,
    // PlanningId already scopes TeamTask for that sprint. Also sending from/to
    // filters by Task.DeadLine and can zero out results (wrong year / deadlines
    // outside the planning week). Use date range only when no planning is selected.
    fromDate: planningId ? null : filters.fromDate || null,
    toDate: planningId ? null : filters.toDate || null,
  };
};

const resolveStatusColor = (name, color) => {
  const key = (name || "").toLowerCase().trim();
  if (STATUS_COLOR_OVERRIDES[key]) return STATUS_COLOR_OVERRIDES[key];
  if (STATUS_COLOR_OVERRIDES[key.replace(/\s+/g, "")]) {
    return STATUS_COLOR_OVERRIDES[key.replace(/\s+/g, "")];
  }
  return color || "#8592A6";
};

export const transformChartData = (rows = []) => {
  if (!rows?.length) return { chartData: [], taskColors: {} };
  const taskColors = {};
  const byProject = rows.reduce((result, current) => {
    let project = result.find((p) => p.labelId === current.projectId);
    if (!project) {
      project = {
        labelId: current.projectId,
        label: current.projectName,
      };
      result.push(project);
    }
    const key = (current.statusCategoryName || `status-${current.statusCategoryId}`).trim();
    project[key] = (project[key] || 0) + (current.taskCount || 0);
    taskColors[key.toLowerCase()] = resolveStatusColor(key, current.color);
    return result;
  }, []);
  return { chartData: byProject, taskColors };
};

/** Canonical status categories — always shown even when count is 0. */
export const DEFAULT_STATUS_CATEGORIES = [
  { statusCategoryId: 1, statusCategoryName: "Not Started", color: "#8592A6" },
  { statusCategoryId: 2, statusCategoryName: "Active", color: "#3B82F6" },
  { statusCategoryId: 3, statusCategoryName: "Testing", color: "#F59E0B" },
  { statusCategoryId: 4, statusCategoryName: "Done", color: "#22C55E" },
  { statusCategoryId: 5, statusCategoryName: "Closed", color: "#8B5CF6" },
];

const normalizeStatusKey = (name) =>
  String(name || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "");

/** API sometimes sends "Close" for the Closed category. */
const statusKeysMatch = (itemKey, defKey) => {
  if (itemKey === defKey) return true;
  if (defKey === "closed" && itemKey === "close") return true;
  return false;
};

export const padStatusBreakdown = (items = []) =>
  DEFAULT_STATUS_CATEGORIES.map((def) => {
    const defKey = normalizeStatusKey(def.statusCategoryName);
    const match = (items || []).find((item) => {
      if (Number(item.statusCategoryId) === def.statusCategoryId) return true;
      return statusKeysMatch(normalizeStatusKey(item.statusCategoryName), defKey);
    });
    return {
      statusCategoryId: def.statusCategoryId,
      // Always use canonical label (e.g. "Closed", never "Close").
      statusCategoryName: def.statusCategoryName,
      taskCount: match?.taskCount || 0,
      color: resolveStatusColor(def.statusCategoryName, match?.color || def.color),
    };
  });

/** Done + Closed as share of total tasks (0–100). */
export const getCompletionProgressPercent = (statusBreakdown = []) => {
  const total = (statusBreakdown || []).reduce((sum, item) => sum + (item.taskCount || 0), 0);
  if (!total) return 0;
  const completed = (statusBreakdown || []).reduce((sum, item) => {
    const id = Number(item.statusCategoryId);
    if (id === 4 || id === 5) return sum + (item.taskCount || 0);
    const key = normalizeStatusKey(item.statusCategoryName);
    if (key === "done" || key === "closed" || key === "close") {
      return sum + (item.taskCount || 0);
    }
    return sum;
  }, 0);
  return clampPercent((completed / total) * 100);
};

export const transformStatusDistribution = (rows = [], summary = {}) => {
  const totals = {};
  (rows || []).forEach((row) => {
    const key = (row.statusCategoryName || `status-${row.statusCategoryId}`).trim();
    if (!totals[key]) {
      totals[key] = {
        label: key,
        data: 0,
        color: resolveStatusColor(key, row.color),
        statusCategoryId: row.statusCategoryId,
      };
    }
    totals[key].data += row.taskCount || 0;
  });

  const padded = padStatusBreakdown(
    Object.values(totals).map((item) => ({
      statusCategoryId: item.statusCategoryId,
      statusCategoryName: item.label,
      taskCount: item.data,
      color: item.color,
    }))
  );

  if (padded.every((s) => !s.taskCount) && summary?.totalTasks) {
    return padded;
  }
  return padded.map((s) => ({
    label: s.statusCategoryName,
    data: s.taskCount,
    color: s.color,
  }));
};

export const countUniqueProjects = (rows = []) => {
  const ids = new Set((rows || []).map((r) => r.projectId).filter((id) => id != null));
  return ids.size;
};

export const sortMembers = (members = [], sortKey = "util-desc") => {
  const list = [...(members || [])];
  switch (sortKey) {
    case "util-asc":
      return list.sort((a, b) => (a.utilization || 0) - (b.utilization || 0));
    case "name-asc":
      return list.sort((a, b) =>
        String(a.userName || "").localeCompare(String(b.userName || ""), undefined, {
          sensitivity: "base",
        })
      );
    case "name-desc":
      return list.sort((a, b) =>
        String(b.userName || "").localeCompare(String(a.userName || ""), undefined, {
          sensitivity: "base",
        })
      );
    case "util-desc":
    default:
      return list.sort((a, b) => (b.utilization || 0) - (a.utilization || 0));
  }
};

export const MEMBER_SORT_OPTIONS = [
  { value: "util-desc", label: "Utilization (High to Low)" },
  { value: "util-asc", label: "Utilization (Low to High)" },
  { value: "name-asc", label: "Name (A–Z)" },
  { value: "name-desc", label: "Name (Z–A)" },
];
